import React, { type ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonIcon, Icon, Typography } from '@saviynt/design-system';
import _ from 'lodash';

import type { RootState } from '../../../../services/serviceTypes';
import {
  openSaveDraftModal,
  resetPromptFooterState,
  setActiveStep,
  setCurrentEntitlementIndex,
  setFormStatus,
  setFullscreenMode,
  setRemoveAccessEntitlementIndex,
} from '../../data-store/copilotSlice';

import './ExpandableForm.css';

interface ExpandableFormProps {
  heading: string;
  stepName: string; // Add stepName to identify which step this form belongs to
  children: ReactNode;
  buttonkind: string;
  buttonLabel: string;
  secondryButtonLabel?: string;
  secondryButtonKind?: string;
  extraBody?: ReactNode;
  isCollapseable?: boolean;
  classname?: string;
  onSave?: () => Promise<boolean> | boolean | void;
  onSecondory?: () => boolean | void;
  isDisabled?: boolean;
  isSecondaryDisabled?: boolean;
  entitlementIndex?: number;
}

/**
 * ExpandableForm
 */
function ExpandableForm({
  isCollapseable,
  heading,
  stepName,
  children,
  extraBody,
  secondryButtonLabel,
  secondryButtonKind,
  buttonLabel,
  buttonkind,
  classname = '',
  onSave,
  onSecondory,
  isDisabled,
  isSecondaryDisabled,
  entitlementIndex,
}: ExpandableFormProps) {
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();

  // Get form status from Redux instead of local state
  const stepFormStatuses = useSelector(
    (state: RootState) => state.copilot.ui.stepFormStatuses
  );

  // Add save draft detection logic
  const { activeStep, stepFormData, savedStepData } = useSelector(
    (
      state: RootState
    ): {
      activeStep: string;
      stepFormData: Record<string, unknown>;
      savedStepData: Record<string, unknown>;
    } => {
      const currentActiveStep = state.copilot.navigation.activeStep;

      return {
        activeStep: currentActiveStep,
        stepFormData: state.copilot.stepFormData[currentActiveStep] ?? {},
        savedStepData: state.copilot.savedStepData[currentActiveStep] ?? {},
      };
    }
  );

  // Check for unsaved changes with special handling for UpdateAccount prefilled data
  const hasUnsavedChanges = (() => {
    // Special case for UpdateAccount: if savedStepData is empty but stepFormData has content,
    // it's likely prefilled data, not user changes
    if (
      activeStep === 'UpdateAccount' &&
      Object.keys(savedStepData).length === 0 &&
      Object.keys(stepFormData).length > 0
    ) {
      return false;
    }

    if (activeStep.includes('Access_')) {
      // For Access steps, only compare mappings (the main user-editable field)
      return !(
        JSON.stringify(stepFormData.mappings) ===
          JSON.stringify(savedStepData.mappings) ||
        Object.keys(savedStepData).length === 0
      );
    }

    // Only check current active step for unsaved changes
    return !(
      _.isEqual(stepFormData, savedStepData) ||
      Object.keys(savedStepData).length === 0
    );
  })();

  const formStatus =
    stepFormStatuses.find((step) => step.stepName === stepName)?.formStatus ??
    'expandableview';

  // Auto-register form in stepFormStatuses when it mounts
  useEffect(() => {
    const existingForm = stepFormStatuses.find(
      (step) => step.stepName === stepName
    );

    if (!existingForm) {
      dispatch(setFormStatus({ stepName, formStatus: 'expandableview' }));
    }
  }, [stepName, stepFormStatuses, dispatch]);

  const handleFullscreen = () => {
    // Use the same context resolution as handleEdit
    const { type, index } = getStepContext();

    // Set entitlement context before opening fullscreen
    switch (type) {
      case 'addAccess':
        dispatch(setCurrentEntitlementIndex(index));
        break;
      case 'removeAccess':
        dispatch(setRemoveAccessEntitlementIndex(index));
        break;
      default:
        break;
    }

    dispatch(setFormStatus({ stepName, formStatus: 'expandedview' }));
    dispatch(
      setFullscreenMode({
        isActive: true,
        currentStep: heading,
        buttonConfig: { kind: buttonkind, label: buttonLabel },
        filledData: {},
        localFields: [],
        entitlementIndex: index >= 0 ? index : undefined,
      })
    );
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const success = await (
        onSave as () => Promise<boolean> | boolean | void
      )();

      if (success) {
        dispatch(setFormStatus({ stepName, formStatus: 'collapsedview' }));
      }
    } catch (err) {
      // Error handled silently
    } finally {
      setIsSaving(false);
    }
  };

  const copilotState = useSelector((state: RootState) => state.copilot);

  const getStepContext = () => {
    if (stepName.startsWith('AddAccess_') || stepName === 'AddAccess') {
      const index =
        entitlementIndex ?? copilotState.entitlementIteration.currentIndex;

      return { type: 'addAccess', index };
    }

    if (stepName.includes('RemoveAccess')) {
      // For RemoveAccess, prioritize entitlementIndex prop, then formData, then Redux state
      let index = entitlementIndex;

      if (index === undefined) {
        // Try to get from current step form data
        const currentStepData = copilotState.stepFormData[stepName];

        if (
          typeof currentStepData === 'object' &&
          'entitlementIndex' in currentStepData
        ) {
          index = currentStepData.entitlementIndex as number;
        } else {
          // Fallback to Redux removeAccessEntitlementIndex
          index = copilotState.removeAccessEntitlementIndex;
        }
      }

      return { type: 'removeAccess', index };
    }

    return { type: 'other', index: -1 };
  };

  const handleEdit = () => {
    // Skip unsaved changes check when editing the current step
    if (activeStep !== stepName && hasUnsavedChanges) {
      dispatch(
        openSaveDraftModal({
          stepName: activeStep,
          targetStep: stepName,
        })
      );

      return;
    }

    // Set entitlement context based on step type
    const { type, index } = getStepContext();

    switch (type) {
      case 'addAccess':
        dispatch(setCurrentEntitlementIndex(index));
        break;
      case 'removeAccess':
        dispatch(setRemoveAccessEntitlementIndex(index));
        break;
      default:
        // No entitlement context needed
        break;
    }

    // Reset prompt footer state
    dispatch(resetPromptFooterState());

    // Navigate to step
    dispatch(setActiveStep(stepName));
    dispatch(setFormStatus({ stepName, formStatus: 'expandableview' }));
  };

  if (formStatus === 'collapsedview' && isCollapseable) {
    return (
      <div className='Integrations-ExpandableForm-Collapsed'>
        <div className='Integrations-ExpandableForm-Collapsed-Header'>
          <div className='Integrations-ExpandableForm-Collapsed-Header-Left'>
            <Icon
              kind='CheckCircleFilledSeparateColors'
              color='--color-foreground-success-subtle'
              size='smallMedium'
            />
            <Typography kind='h2' desktop='h2-d' mobile='h2-d'>
              {heading}
            </Typography>
          </div>
          <div className='Integrations-ExpandableForm-Collapsed-Header-Right'>
            <ButtonIcon
              size='large'
              kind='outlinedNeutral'
              icon={<Icon kind='Edit' size='medium' />}
              onClick={handleEdit}
            />
          </div>
        </div>
      </div>
    );
  }

  const handleSecondryButton = () => {
    try {
      setIsSaving(true);
      (onSecondory as () => boolean | void)();

      dispatch(setFormStatus({ stepName, formStatus: 'collapsedview' }));
    } catch (err) {
      // Error handled silently
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={`Integrations-ExpandableForm ${classname}`}
      data-step={stepName}>
      <div className='Integrations-ExpandableForm-header'>
        <div className='Integrations-ExpandableForm-header-Left'>
          <Icon kind='StatusToDo' size='medium' color='--color-warning-500' />
          <Typography kind='h2' desktop='h2-d' mobile='h2-d'>
            {heading}
          </Typography>
        </div>
        <div className='Integrations-ExpandableForm-header-Right'>
          <ButtonIcon
            kind='ghost'
            icon={<Icon kind='Fullscreen' />}
            onClick={handleFullscreen as () => void}
          />
        </div>
      </div>

      <div className='Integrations-ExpandableForm-Body'>{children}</div>
      <div className='Integrations-ExpandableForm-Body-Button'>{extraBody}</div>

      <div
        className={`${
          extraBody
            ? 'Integrations-ExpandableForm-Footer-Border'
            : 'Integrations-ExpandableForm-Footer'
        }`}>
        {secondryButtonLabel && (
          <Button
            kind={secondryButtonKind}
            onClick={handleSecondryButton}
            type='button'
            isDisabled={isSecondaryDisabled}>
            {secondryButtonLabel}
          </Button>
        )}
        <Button
          kind='filled'
          onClick={handleSave}
          type='button'
          data-action='save'
          isDisabled={isSaving || isDisabled}>
          {buttonLabel}
        </Button>
      </div>
    </div>
  );
}

ExpandableForm.defaultProps = {
  onSave: () => {},
  classname: '',
  isCollapseable: true,
  extraBody: null,
  secondryButtonLabel: '',
  secondryButtonKind: 'outlined',
  isSecondaryDisabled: false,
  isDisabled: false,
  onSecondory: () => {},
  entitlementIndex: -1,
};

export default ExpandableForm;
